/**
 * User Settings V2 — "Access Control" section (admin-only): customer +
 * role assignment, blanket / per-site-group access, and the
 * staff/superuser flags. Combines what the legacy edit-user
 * page split between Permissions and Site Groups tabs.
 *
 * Contains:
 * - AccessControlSection — the rendered SettingsSection (default export)
 * - CustomerField, AuthGroupsField, StaffAuthGroupsField, SiteGroupAccessField
 * - CustomerChangeSiteGroupReset — clears dependent access fields on customer change
 * - useRequesterCapabilities
 */

import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import {
  Alert,
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useField, useFormikContext } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { fetchSiteGroupsForCustomer } from '@sophocles/api/siteGroup';
import { fetchEligibleUserGroups } from '@sophocles/api/user';
import CustomerAutocomplete from '@sophocles/components/CustomerAutocomplete';
import StatusChip from '@sophocles/components/StatusChip';
import { getAuthUserSelector } from '@sophocles/features/auth/authSelectors';
import { selectSeparateTenant } from '@sophocles/features/tenant/slice';
import useFetchedList from '@sophocles/hooks/useFetchedList';
import {
  determineIsAtLeastCustomerAdmin,
  determineIsResellerUser,
  determineIsSiteGroupAdmin,
  roleDisplayNameSwitch,
} from '@sophocles/utils/userPermissionUtils';
import { SettingsSection, SettingsSubsection, SettingsSwitchField } from '@sophocles/views/settings/components';

import {
  addonGroupLabel,
  BASIC_AUTH_GROUP_IDS,
  BASIC_AUTH_GROUPS,
  basicGroupLabel,
  STAFF_GROUP_BUCKETS,
  VIEW_ONLY_ADDON_GROUPS,
} from './authGroupConstants';

// Formik adapter for CustomerAutocomplete.  Wraps the existing
// component in a useField hook so changes flow through the unified
// save bar rather than triggering an immediate write.
function CustomerField({ name, disabled }) {
  const { t } = useTranslation();
  const [field, , helpers] = useField(name);
  // The picker's width floor is wider than a small phone's content column, so
  // it holds from `sm` up only — below that the field takes the width it has.
  return (
    <Box sx={{ minWidth: { xs: 0, sm: 280 }, maxWidth: 480 }}>
      <CustomerAutocomplete
        name={name}
        label={t('general.customer', 'Customer')}
        value={field.value || null}
        disabled={disabled}
        fullWidth
        onChange={(_evt, customerId) => helpers.setValue(customerId ?? null)}
      />
    </Box>
  );
}

// Formik-bound staff auth-group editor. Drives the same
// `permissions.group_ids` field as the non-staff editor but with a
// different option set — the staff taxonomy (sales, tech-support,
// accounting, odl-engineering, etc.) — fetched per-target from
// /users/<id>/eligibleusergroups/. Selections outside the eligible
// list are preserved as passthrough so a round-trip PATCH doesn't
// drop holdover memberships an admin can't reassign.
//
// Renders as a flat checkbox list (one row per eligible group)
// matching the v1.7.6 ``AuthGroupSelectionTable`` paradigm — every
// option is always visible, multi-select via native checkbox state,
// no dropdown to click through. Labels resolve through
// ``roleDisplayNameSwitch`` (the canonical group-id → display-name
// map) with a fallback to the backend's group ``name`` field.
function StaffAuthGroupsField({ targetUserId, disabled }) {
  const { t } = useTranslation();
  const [field, , helpers] = useField('permissions.group_ids');
  const groupIds = field.value || [];

  const fetchEligible = React.useCallback(() => {
    if (!targetUserId) return Promise.resolve([]);
    return fetchEligibleUserGroups(targetUserId)
      .then(data => data?.results || data || [])
      .catch(() => []);
  }, [targetUserId]);
  const { items: eligible } = useFetchedList(fetchEligible, [fetchEligible], { initial: null });

  const eligibleList = React.useMemo(() => {
    if (!Array.isArray(eligible)) return [];
    return eligible.map(g =>
      typeof g === 'object' && g !== null ? { id: g.id, name: g.name } : { id: g, name: null },
    );
  }, [eligible]);

  const eligibleIds = React.useMemo(() => new Set(eligibleList.map(g => g.id)), [eligibleList]);
  const selectedSet = React.useMemo(() => new Set(groupIds), [groupIds]);
  // Passthrough = anything currently held that the requester can't
  // see in their eligible list — preserved through edits.
  const passthroughIds = groupIds.filter(id => eligible !== null && !eligibleIds.has(id));

  const labelFor = g => roleDisplayNameSwitch(g.id, true, t) || g.name || `#${g.id}`;

  const toggle = id => {
    const next = new Set(selectedSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    // Keep passthrough at the end so they round-trip on save.
    const editableIds = eligibleList.map(g => g.id).filter(gid => next.has(gid));
    helpers.setValue([...editableIds, ...passthroughIds]);
  };

  // Sectioned layout — grouped by role family so the admin-edit UI
  // doesn't degenerate into one long undifferentiated checkbox list.
  // Buckets render only when at least one eligible group lands in
  // them; anything not in a named bucket falls through to "Other".
  const buckets = React.useMemo(() => {
    const remaining = new Set(eligibleList.map(g => g.id));
    const grouped = STAFF_GROUP_BUCKETS.map(bucket => {
      const members = eligibleList.filter(g => bucket.ids.has(g.id));
      members.forEach(g => remaining.delete(g.id));
      return { ...bucket, members };
    });
    const others = eligibleList.filter(g => remaining.has(g.id));
    if (others.length > 0) {
      grouped.push({
        labelKey: 'userSettings.staffGroups.other',
        labelDefault: 'Other',
        members: others,
      });
    }
    return grouped
      .map(b => ({
        ...b,
        members: [...b.members].sort((a, b2) => labelFor(a).localeCompare(labelFor(b2))),
      }))
      .filter(b => b.members.length > 0);
  }, [eligibleList, t]);

  if (eligible === null) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="caption" color="text.secondary">
          {t('userSettings.loadingStaffGroups', 'Loading staff groups…')}
        </Typography>
      </Box>
    );
  }

  if (eligibleList.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('userSettings.noStaffGroupsEligible', 'No staff groups available to assign.')}
      </Typography>
    );
  }

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {t('userSettings.staffGroups', 'Staff Groups')}
      </Typography>
      <Stack spacing={2}>
        {buckets.map(bucket => (
          <Box key={bucket.labelKey}>
            <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.25, lineHeight: 1.4 }}>
              {t(bucket.labelKey, bucket.labelDefault)}
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                columnGap: 2,
                rowGap: 0,
              }}
            >
              {bucket.members.map(g => (
                <FormControlLabel
                  key={g.id}
                  control={
                    <Checkbox
                      checked={selectedSet.has(g.id)}
                      onChange={() => toggle(g.id)}
                      disabled={disabled}
                      size="small"
                    />
                  }
                  label={labelFor(g)}
                  sx={{ mr: 0 }}
                />
              ))}
            </Box>
          </Box>
        ))}
      </Stack>
      {passthroughIds.length > 0 ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          {t(
            'userSettings.staffGroupsPassthrough',
            'Held memberships not editable here ({{count}}) will be preserved on save.',
            { count: passthroughIds.length },
          )}
        </Typography>
      ) : null}
    </Box>
  );
}

// Formik-bound auth-group editor.  Reads `permissions.group_ids`
// (combined list of basic + addon group ids) and renders a single
// Select for the basic role plus checkboxes for the addons.
// Visibility + option set follow the requester's capabilities — a
// customer admin won't see Reseller, a site-group manager won't
// see Admin, etc.  When the target is a staff user (customer 1 +
// is_staff), defers to StaffAuthGroupsField which uses the staff
// taxonomy instead.
function AuthGroupsField({
  canCreateAdmins,
  canCreateManagers,
  canCreateResellers,
  isStaffEdit,
  targetUserId,
  disabled,
}) {
  const { t } = useTranslation();
  const [field, , helpers] = useField('permissions.group_ids');
  const groupIds = field.value || [];
  const basicId = groupIds.find(id => BASIC_AUTH_GROUP_IDS.includes(id)) ?? '';
  const addonIds = groupIds.filter(id => VIEW_ONLY_ADDON_GROUPS.includes(id));
  // Group memberships outside the basic + view-only-addon split are
  // preserved untouched (e.g. addon-api-user / addon-api-manager
  // managed elsewhere, plus staff-only memberships when admin
  // doesn't have edit privilege over them).  Round-trip PATCH must
  // not silently drop them.
  const passthroughIds = groupIds.filter(
    id => !BASIC_AUTH_GROUP_IDS.includes(id) && !VIEW_ONLY_ADDON_GROUPS.includes(id),
  );

  const setBasic = newBasic => {
    // Switching basic role clears the view-only addons (legacy
    // behavior — those addons only make sense for View-only).
    // Passthrough memberships are preserved.
    if (newBasic === basicId) return;
    helpers.setValue([...(newBasic ? [newBasic] : []), ...passthroughIds]);
  };
  const toggleAddon = id => {
    const next = addonIds.includes(id) ? addonIds.filter(x => x !== id) : [...addonIds, id];
    helpers.setValue([...(basicId ? [basicId] : []), ...next, ...passthroughIds]);
  };

  const basicOptions = [
    canCreateResellers && BASIC_AUTH_GROUPS.RESELLER,
    canCreateAdmins && BASIC_AUTH_GROUPS.ADMIN,
    canCreateManagers && BASIC_AUTH_GROUPS.MANAGER,
    BASIC_AUTH_GROUPS.USER,
    BASIC_AUTH_GROUPS.VIEW_ONLY,
    BASIC_AUTH_GROUPS.THIRD_PARTY_ADMIN,
  ].filter(Boolean);

  // Addons are only meaningful for View-only — matches the legacy
  // NonStaffAuthGroupSelectionComponent which gates the sub-roles
  // panel on `mainAuthGroup === 1054` exclusively.  Plain User role
  // doesn't get them.
  const showAddons = basicId === BASIC_AUTH_GROUPS.VIEW_ONLY;

  if (isStaffEdit) {
    // Staff users on customer 1 use a different group taxonomy
    // (sales, tech-support, accounting, etc.) — fetched from the
    // /users/<id>/eligibleusergroups/ endpoint.  Renders as a
    // multi-select bound to the same `permissions.group_ids`
    // field; passthrough memberships outside the eligible list
    // (e.g. holdovers an admin can't reassign) are preserved.
    return <StaffAuthGroupsField targetUserId={targetUserId} disabled={disabled} />;
  }

  return (
    <Stack spacing={2}>
      <TextField
        select
        label={t('general.role', 'Role')}
        value={basicId || ''}
        onChange={e => setBasic(e.target.value || null)}
        disabled={disabled}
        size="small"
        fullWidth
      >
        <MenuItem value="">
          <em>{t('userSettings.selectRolePrompt', 'Select a role')}</em>
        </MenuItem>
        {basicOptions.map(id => (
          <MenuItem key={id} value={id}>
            {basicGroupLabel(id, t)}
          </MenuItem>
        ))}
      </TextField>

      {showAddons ? (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            {t('userSettings.addonRoles', 'Add-on Roles')}
          </Typography>
          <Stack>
            {VIEW_ONLY_ADDON_GROUPS.map(id => (
              <FormControlLabel
                key={id}
                control={
                  <Checkbox checked={addonIds.includes(id)} onChange={() => toggleAddon(id)} disabled={disabled} />
                }
                label={addonGroupLabel(id, t)}
              />
            ))}
          </Stack>
        </Box>
      ) : null}
    </Stack>
  );
}

// Formik-bound site-group multi-select.  Fetches the available
// site groups for the target's customer (per the legacy
// /customers/<id>/sitegroups/ endpoint) and binds the selection
// to `access.site_group_ids`.  The selected list is the source of
// truth — labels for already-selected items come from
// settings.access.site_groups so we render them even before the
// available-list fetch resolves.
function SiteGroupAccessField({ customerId, fallbackSiteGroups, disabled }) {
  const { t } = useTranslation();
  const [field, , helpers] = useField('access.site_group_ids');
  const selectedIds = field.value || [];

  const fetchAvailable = React.useCallback(() => {
    if (!customerId) return Promise.resolve([]);
    return fetchSiteGroupsForCustomer(customerId)
      .then(data => data?.results || data || [])
      .catch(() => []);
  }, [customerId]);
  const { items: available } = useFetchedList(fetchAvailable, [fetchAvailable], { initial: null });

  // Build a display map: prefer the available-list (full record),
  // fall back to whatever the settings response returned for
  // already-selected ids.
  const labelMap = React.useMemo(() => {
    const map = {};
    (fallbackSiteGroups || []).forEach(sg => {
      map[sg.id] = sg.name;
    });
    (available || []).forEach(sg => {
      map[sg.id] = sg.name;
    });
    return map;
  }, [available, fallbackSiteGroups]);

  const options = available || fallbackSiteGroups || [];
  const valueObjects = selectedIds.map(id => ({ id, name: labelMap[id] || `#${id}` }));

  return (
    <Autocomplete
      multiple
      disabled={disabled || available === null}
      loading={available === null}
      options={options}
      value={valueObjects}
      onChange={(_evt, next) => helpers.setValue(next.map(o => o.id))}
      isOptionEqualToValue={(opt, val) => opt.id === val.id}
      getOptionLabel={opt => opt.name || `#${opt.id}`}
      filterSelectedOptions
      renderTags={(value, getTagProps) =>
        value.map((opt, idx) => {
          const tagProps = getTagProps({ index: idx });
          return <Chip {...tagProps} key={opt.id} label={opt.name || `#${opt.id}`} size="small" />;
        })
      }
      renderInput={params => (
        <TextField
          {...params}
          label={t('userSettings.siteGroups', 'Site Groups')}
          size="small"
          placeholder={
            selectedIds.length === 0 ? t('userSettings.noSiteGroups', 'No site groups assigned.') : undefined
          }
        />
      )}
    />
  );
}

// Effect: when the form's customer_id changes from a previously
// non-null value, clear the user's site-group access (the existing
// site_group_ids belong to the old customer's universe and won't
// validate).  Mirrors the legacy PermissionsTab behavior:
// `setSelectedSiteGroups(Set([]))` on customer change.  Also drops
// the `all_sitegroups_user` blanket flag since blanket access is
// per-customer too.
function CustomerChangeSiteGroupReset() {
  const { values, setFieldValue } = useFormikContext();
  const formCustomerId = values.permissions?.customer_id ?? null;
  const lastSeen = React.useRef(formCustomerId);
  React.useEffect(() => {
    if (lastSeen.current !== null && lastSeen.current !== formCustomerId) {
      // Customer changed (not just the initial load) — wipe the
      // dependent access fields.
      setFieldValue('access.site_group_ids', [], false);
      setFieldValue('permissions.all_sitegroups_user', false, false);
    }
    lastSeen.current = formCustomerId;
  }, [formCustomerId, setFieldValue]);
  return null;
}

// Compute requester capabilities for gating role assignment.  These
// mirror the legacy PermissionsTab's `canCreate*` flags so the
// option set a customer admin sees in V2 matches what they saw
// before.
function useRequesterCapabilities() {
  const authUser = useSelector(getAuthUserSelector);
  return React.useMemo(() => {
    const isStaff = authUser?.get?.('is_staff', false) ?? false;
    const isSuper = authUser?.get?.('is_superuser', false) ?? false;
    const groupIds = (authUser?.get?.('groups')?.toJS?.() || authUser?.get?.('groups') || [])
      .map(g => (typeof g === 'object' ? g.id : g))
      .filter(Boolean);
    return {
      isStaff,
      isSuper,
      isResellerUser: determineIsResellerUser(groupIds),
      isCustomerAdmin: isStaff || determineIsAtLeastCustomerAdmin(groupIds),
      isSiteGroupAdmin: isStaff || determineIsSiteGroupAdmin(groupIds),
    };
  }, [authUser]);
}

// Access Control section — admin-only.  Combines what the legacy
// edit-user page split between Permissions and Site Groups tabs:
// customer + role assignment, blanket / per-site access, and the
// staff/superuser flags. Account deletion lives in UserDangerZone.
//
// Field-level gating mirrors the legacy PermissionsTab and
// SiteGroupsTab:
// - Customer picker: requester must be staff or a reseller user.
// - all_sitegroups_user toggle: requester must hold the flag, be
//   superuser, or be a customer-admin / reseller-user.
// - is_staff toggle: only when target's customer is ODL (id 1)
//   and the requester is staff.
// - is_superuser toggle: only when the target is staff and the
//   requester is super.
// - Basic auth group: option set narrows by requester role
//   (admins grant admins; managers grant managers).
// - Addons: only when basic role is View-only.
//
// Site-group selection is gated on a customer being chosen first
// (no customer → no list to pick from), and the selection is
// auto-cleared when the customer changes.
export default function AccessControlSection({ permissions, access, targetUserId }) {
  const { t } = useTranslation();
  const caps = useRequesterCapabilities();
  const { values } = useFormikContext();
  const separateTenant = useSelector(selectSeparateTenant);
  const formCustomerId = values.permissions?.customer_id ?? permissions.customer?.id ?? null;
  const formIsStaff = !!values.permissions?.is_staff;
  const formAllSiteGroups = !!values.permissions?.all_sitegroups_user;
  const formSiteGroupIds = values.access?.site_group_ids || [];

  const canChangeCustomer = caps.isStaff || caps.isResellerUser;
  const canSetAllSiteGroups = caps.isStaff || caps.isCustomerAdmin || caps.isResellerUser;
  const showStaffToggle = caps.isStaff && formCustomerId === 1;
  const showSuperToggle = caps.isSuper && formIsStaff;
  // Customer 1 + is_staff is the staff-edit branch; auth groups
  // there belong to the staff taxonomy (sales, tech-support, etc.).
  const isStaffEdit = formCustomerId === 1 && formIsStaff;

  return (
    <SettingsSection
      id="settings-access"
      icon={<AdminPanelSettingsIcon />}
      title={t('userSettings.accessControl', 'Access Control')}
      description={t(
        'userSettings.v2.accessControlDescription',
        "Customer assignment, role, and the user's site-group access.",
      )}
      chip={<StatusChip label={t('userSettings.adminOnly', 'Admin Only')} color="info" />}
    >
      <CustomerChangeSiteGroupReset />
      <Stack spacing={3}>
        <SettingsSubsection title={t('userSettings.customerAndRoles', 'Customer & Role')}>
          <Stack spacing={2}>
            {canChangeCustomer ? (
              <CustomerField name="permissions.customer_id" />
            ) : (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  {t('general.customer', 'Customer')}
                </Typography>
                <Typography variant="body1">
                  {permissions.customer?.name || t('userSettings.noCustomer', 'No customer')}
                </Typography>
              </Box>
            )}
            {!(separateTenant && formCustomerId === 1 && formIsStaff) &&
              (formCustomerId ? (
                <AuthGroupsField
                  canCreateAdmins={caps.isCustomerAdmin}
                  canCreateManagers={caps.isSiteGroupAdmin}
                  canCreateResellers={caps.isStaff || caps.isResellerUser}
                  isStaffEdit={isStaffEdit}
                  targetUserId={targetUserId}
                />
              ) : (
                <Alert severity="info" variant="outlined">
                  {t('userSettings.v2.pickCustomerFirst', 'Pick a customer before assigning a role.')}
                </Alert>
              ))}
          </Stack>
        </SettingsSubsection>

        <SettingsSubsection title={t('userSettings.siteGroupAccess', 'Site Group Access')}>
          {!formCustomerId ? (
            <Alert severity="info" variant="outlined">
              {t('userSettings.v2.pickCustomerForSiteGroups', 'Pick a customer to see available site groups.')}
            </Alert>
          ) : (
            <Stack spacing={2}>
              <SettingsSwitchField
                name="permissions.all_sitegroups_user"
                label={t('userSettings.accessAllSiteGroups', 'Access all site groups')}
                helperText={
                  canSetAllSiteGroups
                    ? t(
                        'userSettings.v2.allSiteGroupsHelper',
                        'Grants this user access to every site group within their customer, including any added later.',
                      )
                    : t(
                        'userSettings.v2.allSiteGroupsLocked',
                        'Only superusers, customer admins, and resellers can change this.',
                      )
                }
                disabled={!canSetAllSiteGroups}
                disableDivider
              />
              {!formAllSiteGroups ? (
                <Box>
                  <SiteGroupAccessField customerId={formCustomerId} fallbackSiteGroups={access.site_groups} />
                  {formSiteGroupIds.length === 0 ? (
                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                      {t('userSettings.mustSelectAtLeastOne', 'Select at least one site group.')}
                    </Typography>
                  ) : null}
                </Box>
              ) : null}
            </Stack>
          )}
        </SettingsSubsection>

        {showStaffToggle || showSuperToggle ? (
          <SettingsSubsection
            title={t('userSettings.internalFlags', 'Internal Flags')}
            chip={<StatusChip label={t('userSettings.staffOnly', 'Staff Only')} color="warning" />}
          >
            <Stack>
              {showStaffToggle ? (
                <SettingsSwitchField
                  name="permissions.is_staff"
                  label={t('userSettings.isStaff', 'Staff')}
                  helperText={t(
                    'userSettings.v2.isStaffHelper',
                    'Internal ODL staff member. Limited to users on the ODL customer record.',
                  )}
                  disableDivider={!showSuperToggle}
                />
              ) : null}
              {showSuperToggle ? (
                <SettingsSwitchField
                  name="permissions.is_superuser"
                  label={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <span>{t('userSettings.isSuperuser', 'Superuser')}</span>
                      <StatusChip label={t('userSettings.superuserOnly', 'Superuser Only')} color="bad" />
                    </Stack>
                  }
                  helperText={t(
                    'userSettings.v2.isSuperuserHelper',
                    'Bypasses all permission checks. Only superusers can grant this.',
                  )}
                  disableDivider
                />
              ) : null}
            </Stack>
          </SettingsSubsection>
        ) : null}
      </Stack>
    </SettingsSection>
  );
}
